import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icons } from "./Icons";
import {
  getAvatarUrl,
  getMyNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  acceptTerms,
} from "../services/api";
import API from "../services/api";
import { useWebSocketEvent } from "../hooks/useWebSocket";

const UserLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userName, setUserName] = useState("User");
  const [avatarPath, setAvatarPath] = useState(null);
  const [userId, setUserId] = useState(null);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());

  // Real notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  const desktopNotificationRef = useRef(null);
  const mobileNotificationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showNotifications &&
        desktopNotificationRef.current && !desktopNotificationRef.current.contains(event.target) &&
        mobileNotificationRef.current && !mobileNotificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.full_name) {
        setUserName(user.full_name);
      }
      if (user && user.profile_picture_path)
        setAvatarPath(user.profile_picture_path);
      if (user && user.user_id) setUserId(user.user_id);
    } catch (e) {
      setUserName("User");
    }
  }, []);

  // Checks if the logged-in user has accepted the Terms and Conditions
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.has_accepted_terms === false) {
        setShowTermsModal(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const handleAcceptTerms = async () => {
    setAcceptingTerms(true);
    try {
      await acceptTerms();
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        user.has_accepted_terms = true;
        localStorage.setItem('user', JSON.stringify(user));
      }
      setShowTermsModal(false);
    } catch (error) {
      console.error('Failed to accept terms:', error);
    } finally {
      setAcceptingTerms(false);
    }
  };

  useEffect(() => {
    const handleAvatarUpdate = () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
          setAvatarPath(user.profile_picture_path || null);
          setUserId(user.user_id || null);
          setAvatarVersion(Date.now());
        }
      } catch (e) { }
    };
    window.addEventListener("avatarUpdated", handleAvatarUpdate);
    return () =>
      window.removeEventListener("avatarUpdated", handleAvatarUpdate);
  }, []);

  // Fetches real notifications from the backend and updates the unread badge
  const fetchNotifications = async () => {
    try {
      const res = await getMyNotifications();
      setNotifications(res.data);
      setUnreadCount(res.data.filter((n) => !n.is_read).length);
    } catch (error) {
      // Silently fail — don't break the layout if this call fails
    }
  };

  // Fetches on mount and then every 60 seconds to keep badge current
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Real-time synchronization
  useWebSocketEvent("NOTIFICATION_UPDATED", () => {
    fetchNotifications();
  });

  // Marks a notification as read when clicked in the dropdown
  const handleNotificationClick = async (notification) => {
    setShowNotifications(false);
    navigate("/org/notifications");
    if (!notification.is_read) {
      try {
        await markNotificationRead(notification.notification_id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notification.notification_id
              ? { ...n, is_read: true }
              : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
  };

  // Marks all notifications as read from the dropdown header button
  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  // Formats a timestamp into a relative time label (e.g. "2h ago")
  const formatTime = (createdAt) => {
    if (!createdAt) return "";
    const diff = Date.now() - new Date(createdAt).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();
  const isOffCampusUser = user?.application_type === "offcampus";

  const menuItems = isOffCampusUser
    ? [
        { name: "Home", icon: Icons.Dashboard, path: "/org/dashboard" },
        { name: "Off-Campus Activity", icon: Icons.Bus, path: "/org/off-campus" },
        { name: "Participants", icon: Icons.Group, path: "/org/participants" },
        {
          name: "Announcements",
          icon: Icons.Announcement,
          path: "/org/announcements",
        },
        {
          name: "Requirements",
          icon: Icons.ClipboardList,
          path: "/org/requirements",
        },
      ]
    : [
        { name: "Home", icon: Icons.Dashboard, path: "/org/dashboard" },
        {
          name: "Accreditation",
          icon: Icons.Accreditation,
          path: "/org/accreditation",
        },
        {
          name: "Announcements",
          icon: Icons.Announcement,
          path: "/org/announcements",
        },
        {
          name: "Requirements",
          icon: Icons.ClipboardList,
          path: "/org/requirements",
        },
      ];

  const avatarSrc =
    avatarPath && userId
      ? `${getAvatarUrl(userId, avatarPath)}?v=${avatarVersion}`
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=0D3B2E&color=fff`;

  const getPageTitle = () => {
    if (location.pathname === "/org/dashboard") return "Home";
    if (location.pathname === "/org/accreditation") return "Accreditation";
    if (location.pathname === "/org/off-campus") return "Off-Campus Activity";
    if (location.pathname === "/org/announcements") return "Announcements";
    if (location.pathname === "/org/requirements") return "Requirements";
    if (location.pathname === "/org/participants") return "Participants";
    if (location.pathname === "/org/profile") return "Profile";
    if (location.pathname === "/org/notifications") return "Notifications";
    return "Home";
  };

  // Shows only the 5 most recent notifications in the dropdown
  const dropdownNotifications = notifications.slice(0, 5);

  // True on screens < 768 px
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  // We re-read this on every render, but for layout decisions that's fine.
  // For a truly reactive version we'd use useState + useEffect, but since
  // the Layout re-renders whenever location changes, this is sufficient.
  const isDashboard = location.pathname === "/org/dashboard";

  return (
    <div className="flex h-screen overflow-hidden bg-background relative">
      {/* Mobile Sidebar Overlay — desktop only drawer, hidden on mobile */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed md:relative z-50 h-full ${isCollapsed ? "w-24" : "w-64"
          } transition-transform duration-300 ease-in-out transform ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          } bg-sidebar text-white flex flex-col justify-between rounded-r-[32px] flex-shrink-0 shadow-xl overflow-hidden py-6`}
      >
        <div>
          {/* Logo Area */}
          <div
            className={`px-4 pb-8 flex items-center ${isCollapsed ? "justify-center flex-col space-y-4" : "justify-between"}`}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 flex-shrink-0">
                <img
                  src="/assets/osas-logo.png"
                  className="w-full h-full object-contain"
                  alt="OSAS Logo"
                />
              </div>
              {!isCollapsed && (
                <div className="ml-3">
                  <h1 className="text-xl font-bold tracking-tight">OSAS</h1>
                  <p className="text-[9px] text-gray-300 leading-tight">
                    Student Organization
                    <br />
                    and Activities
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="text-gray-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
            >
              {isCollapsed ? <Icons.ChevronRight /> : <Icons.ChevronLeft />}
            </button>
          </div>

          {/* Menu */}
          <div className={isCollapsed ? "px-3" : "px-4"}>
            {!isCollapsed && (
              <p className="text-[10px] text-gray-400 font-semibold mb-3 px-4 uppercase tracking-wider transition-opacity duration-300">
                Menu
              </p>
            )}
            <nav className="space-y-2">
              {menuItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center ${isCollapsed ? "justify-center px-0" : "space-x-3 px-4"} py-3 rounded-xl transition-colors ${isActive ? "text-white font-semibold shadow-md" : "text-gray-300 hover:bg-white/10"}`}
                    style={
                      isActive
                        ? {
                          background:
                            "linear-gradient(to right, #086C53 99%, rgba(255, 255, 255, 1) 100%)",
                        }
                        : {}
                    }
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon />
                    {!isCollapsed && (
                      <span className="text-sm font-medium whitespace-nowrap">
                        {item.name}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className={`mt-auto pt-6 ${isCollapsed ? "px-3" : "px-4"}`}>
          <button
            onClick={() => setShowLogoutConfirm(true)}
            title={isCollapsed ? "Log out" : undefined}
            className={`w-full flex items-center ${isCollapsed ? "justify-center px-0" : "space-x-3 px-4"} py-3 rounded-xl text-gray-300 hover:bg-white/10 transition-colors`}
          >
            <Icons.LogOut />
            {!isCollapsed && (
              <span className="text-sm font-medium whitespace-nowrap">
                Log out
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden w-full">
        {/* Desktop Topbar */}
        <header className="hidden md:flex h-20 bg-white border-b border-gray-200 items-center justify-between px-10 flex-shrink-0 sticky top-0 z-10 shadow-sm">
          <div className="flex items-center space-x-3">
            <button
              className="md:hidden text-gray-600 hover:text-gray-900 focus:outline-none p-1"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 truncate">{getPageTitle()}</h2>
          </div>

          <div className="flex items-center space-x-6 relative" ref={desktopNotificationRef}>
            {/* Notification Bell — real unread count badge */}
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`transition-colors relative ${showNotifications ? "text-[#086C53]" : "text-gray-400 hover:text-gray-600"}`}
            >
              <Icons.Bell />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
              )}
            </button>

            {/* Real Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute top-12 right-12 w-80 bg-white rounded-[16px] shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col">
                <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-bold text-[14px] text-gray-800">
                    Notifications
                    {unreadCount > 0 && (
                      <span className="ml-2 bg-red-100 text-red-600 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-[11px] text-[#086C53] font-semibold hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="max-h-[340px] overflow-y-auto divide-y divide-gray-50">
                  {dropdownNotifications.length === 0 ? (
                    <div className="p-5 text-center text-[13px] text-gray-400">
                      No notifications yet.
                    </div>
                  ) : (
                    dropdownNotifications.map((n) => (
                      <div
                        key={n.notification_id}
                        onClick={() => handleNotificationClick(n)}
                        className={`p-5 hover:bg-gray-50 transition-colors cursor-pointer ${!n.is_read ? "bg-[#f0f9f6]/40" : ""}`}
                      >
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="flex items-center space-x-2">
                            {!n.is_read && (
                              <span className="w-2 h-2 rounded-full bg-[#086C53] flex-shrink-0"></span>
                            )}
                            <h4
                              className={`text-[13px] ${!n.is_read ? "font-bold text-gray-900" : "font-semibold text-gray-700"}`}
                            >
                              {n.title}
                            </h4>
                          </div>
                          <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap ml-3">
                            {formatTime(n.created_at)}
                          </span>
                        </div>
                        <p
                          className={`text-[12px] text-gray-500 leading-relaxed ${!n.is_read ? "ml-4" : ""}`}
                        >
                          {n.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 text-center border-t border-gray-100 bg-gray-50/50">
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      navigate("/org/notifications");
                    }}
                    className="text-[12px] font-bold text-gray-600 hover:text-[#086C53] transition-colors"
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            )}

            <Link
              to="/org/profile"
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 overflow-hidden border border-gray-200 shadow-sm">
                <img src={avatarSrc} alt="User Avatar" />
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-bold text-gray-800 leading-tight">
                  {userName}
                </p>
                <p className="text-[11px] text-gray-500 font-medium">
                  {isOffCampusUser ? "Applicant" : "President"}
                </p>
              </div>
            </Link>
          </div>
        </header>

        {/* Mobile Topbar */}
        <header className="md:hidden h-16 bg-[#054334] text-white flex items-center justify-between px-5 flex-shrink-0 sticky top-0 z-20 shadow-sm">
          <h2 className="text-lg font-bold tracking-tight truncate">{getPageTitle()}</h2>
          <div className="flex items-center space-x-4 relative" ref={mobileNotificationRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative text-white hover:text-gray-200 transition-colors"
            >
              <Icons.Bell />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#054334]"></span>
              )}
            </button>
            <Link to="/org/profile">
              <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/40 shadow-sm">
                <img src={avatarSrc} alt="User Avatar" className="w-full h-full object-cover" />
              </div>
            </Link>

            {/* Mobile Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute top-12 right-0 w-72 bg-white rounded-[16px] shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col text-gray-900">
                <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-bold text-[13px] text-gray-800">
                    Notifications
                    {unreadCount > 0 && (
                      <span className="ml-2 bg-red-100 text-red-600 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-[10px] text-[#086C53] font-semibold hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-50">
                  {dropdownNotifications.length === 0 ? (
                    <div className="p-4 text-center text-[12px] text-gray-400">
                      No notifications yet.
                    </div>
                  ) : (
                    dropdownNotifications.map((n) => (
                      <div
                        key={n.notification_id}
                        onClick={() => handleNotificationClick(n)}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${!n.is_read ? "bg-[#f0f9f6]/40" : ""}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center space-x-2">
                            {!n.is_read && (
                              <span className="w-2 h-2 rounded-full bg-[#086C53] flex-shrink-0"></span>
                            )}
                            <h4 className={`text-[12px] ${!n.is_read ? "font-bold text-gray-900" : "font-semibold text-gray-700"}`}>
                              {n.title}
                            </h4>
                          </div>
                          <span className="text-[9px] text-gray-400 font-medium whitespace-nowrap ml-2">
                            {formatTime(n.created_at)}
                          </span>
                        </div>
                        <p className={`text-[11px] text-gray-500 leading-relaxed ${!n.is_read ? "ml-4" : ""}`}>
                          {n.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 text-center border-t border-gray-100 bg-gray-50/50">
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      navigate("/org/notifications");
                    }}
                    className="text-[11px] font-bold text-gray-600 hover:text-[#086C53] transition-colors"
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page Content Area */}
        <main className={`flex-1 overflow-y-auto relative md:p-10 ${isMobile
            ? (isDashboard ? 'p-0 pb-32' : 'p-3 sm:p-4 pb-32')
            : 'p-4'
          }`}>
          <div className={isDashboard ? 'md:max-w-6xl md:mx-auto' : 'max-w-6xl mx-auto'}>{children}</div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div
            className="bg-white rounded-[24px] w-full max-w-[400px] shadow-2xl overflow-hidden flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2"
            >
              <Icons.Close />
            </button>
            <div className="p-8 pb-6 text-center pt-10">
              <h2 className="text-[17px] font-bold text-gray-900 px-4 mb-2">
                Are you sure you want to log out?
              </h2>
            </div>
            <div className="p-6 pt-0 flex justify-center space-x-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogoutConfirm(false);
                  const refreshToken = localStorage.getItem("refresh_token");
                  if (refreshToken) {
                    API.post("/auth/logout", { refresh_token: refreshToken }).catch(
                      () => { },
                    );
                  }
                  localStorage.removeItem("token");
                  localStorage.removeItem("refresh_token");
                  localStorage.removeItem("user");
                  navigate("/login");
                }}
                className="px-6 py-2.5 bg-red-500 rounded-lg text-sm font-semibold text-white hover:bg-red-600 transition-colors shadow-sm"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <nav className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[90%] max-w-[300px] z-[100] md:hidden">
        <div className="bg-[#054334] rounded-full shadow-2xl px-2 py-3">
          <div className="flex justify-around items-center">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex flex-col items-center justify-center flex-1 py-1"
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all ${isActive ? 'bg-white/20 text-white' : 'bg-transparent text-white/50'
                    }`}>
                    <item.icon />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {showTermsModal && (
        <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 bg-[#054334] rounded-t-2xl">
              <h2 className="text-[20px] font-bold text-white">Terms and Conditions & Privacy Policy</h2>
              <p className="text-[13px] text-white/70 mt-1">Please read and accept our terms before continuing.</p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 text-[13px] text-gray-700 leading-relaxed">

              <div>
                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">OrgPass — SLSU-OSAS</p>
                <p className="text-[11px] text-gray-400">Effective Date: Academic Year 2025-2026</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">1. Acceptance of Terms</h3>
                <p>By registering and using OrgPass, you agree to these Terms and Conditions. If you do not agree, do not use the system. These terms apply to all users including organization presidents, advisers, and OSAS administrators.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">2. Purpose of the System</h3>
                <p>OrgPass is an official document submission and validation system developed for SLSU-OSAS to digitize the student organization accreditation and off-campus activity approval process. It is intended solely for use by registered SLSU student organizations and OSAS staff.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">3. User Accounts and Eligibility</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Only users with a valid <strong>@slsu.edu.ph</strong> or <strong>@gmail.com</strong> email address may register</li>
                  <li>Each student organization may only have one registered account</li>
                  <li>Users are responsible for maintaining the confidentiality of their credentials</li>
                  <li>Accounts are non-transferable</li>
                  <li>OSAS reserves the right to suspend or terminate accounts that violate these terms</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">4. Document Submission</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Users are responsible for the accuracy and authenticity of all submitted documents</li>
                  <li>Submission of falsified, altered, or fraudulent documents is strictly prohibited</li>
                  <li>Submitted documents must comply with OSAS requirements and CMO No. 63 s. 2017</li>
                  <li>Documents submitted through OrgPass are official submissions to SLSU-OSAS</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">5. Artificial Intelligence (AI) Document Validation</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>OrgPass uses Google Gemini AI to perform automated document validation</li>
                  <li>AI validation is assistive only — final decisions are made solely by OSAS administrators</li>
                  <li>AI validation results may not be 100% accurate</li>
                  <li>By submitting documents, you consent to their analysis by Google Gemini AI</li>
                  <li>Your submitted documents are NOT used to train Google's AI models</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">6. Data Collection and Use</h3>
                <p>We collect your personal information (name, email, organization details) and submitted documents solely for processing accreditation and off-campus activity applications. This data is not shared with unauthorized parties.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">7. Data Sharing with Third-Party Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-semibold">Google Gemini AI</span> — Document validation only</li>
                  <li><span className="font-semibold">A cloud storage service</span> — Template file storage</li>
                  <li><span className="font-semibold">An email delivery service</span> — Email notifications</li>
                  <li><span className="font-semibold">A secure cloud server</span> — System hosting</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">8. Data Storage and Security</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>All data is transmitted using HTTPS/TLS encryption</li>
                  <li>Passwords are stored as cryptographic hash — never in plain text</li>
                  <li>Access is restricted to authorized users only</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">9. Data Retention</h3>
                <p>Documents and accounts are retained for the academic year and deleted during the annual system reset. Organization records are kept for continuity.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">10. User Rights</h3>
                <p>You may view your submissions, cancel unreviewed documents, and request account deletion by contacting OSAS at orgpass.slsu@gmail.com.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">11. Limitation of Liability</h3>
                <p>OrgPass is provided as-is. SLSU-OSAS is not liable for technical issues or AI validation inaccuracies. Users are solely responsible for their submitted documents.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">12. Contact</h3>
                <p>SLSU Office of Student Affairs and Services (OSAS)<br/>Email: orgpass.slsu@gmail.com</p>
              </div>

            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleAcceptTerms}
                disabled={acceptingTerms}
                className="px-6 py-2.5 bg-[#086C53] text-white rounded-lg text-[14px] font-bold hover:bg-[#054334] transition-colors disabled:opacity-60"
              >
                {acceptingTerms ? 'Saving...' : 'I Agree & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserLayout;
